/**
 * Evil Pressure, Stability, and town reversion (HEX-123 / ADR-0018 §5).
 *
 * Liberation is not permanent. Each liberated town has a Stability `S` that
 * drains each world tick by:
 *
 *   pressure(town) = evilNeighbours − liberatedNeighbours + corruption(town)
 *
 * Evaluation is LAZY: Stability is not simulated every tick. When a town is
 * read/approached, compute ticks elapsed since the last cached value and apply
 * `S -= pressure × ticksElapsed` in one step.
 *
 * Town state transitions:
 *   S ≥ STABILITY_THREAT_THRESHOLD → 'liberated'
 *   0 < S < STABILITY_THREAT_THRESHOLD → 'under-threat'
 *   S ≤ 0 → 'evil' (boss re-challengeable; tickFallen recorded)
 *
 * The spawn town at (1, 1) is hard-immune to reversion (isSpawnTown guard).
 *
 * Evil-weight reconciliation:
 *   corruption() uses EVIL_WEIGHT from src/worldgen/biome-transition.ts
 *   (fractional 0.1–0.5): this is the RIGHT source for a continuous corruption
 *   term that scales with distance. The integer BIOME_EVIL_WEIGHT in
 *   bossEncounter.ts serves a different purpose — it bumps the discrete boss
 *   tier index by a small integer; mixing the two would misscale one or the
 *   other. Keeping them separate is intentional and documented here.
 */

import type { Tile, TownLifecycleState, TownState } from '../core/tile';
import { getTownState, isSpawnTown } from '../lifecycle/townState';
import { EVIL_WEIGHT } from '../worldgen/biome';
import { originDistance } from '../worldgen/distance';
import { neighboursWithin50 } from '../worldgen/frontierSettlement';

// ─── tuning constants ────────────────────────────────────────────────────────

/** Maximum stability (and starting value on liberation). */
export const STABILITY_MAX = 100;

/**
 * Stability below which the town enters the 'under-threat' warning state.
 * At this point players can see the town is at risk without it having fallen.
 */
export const STABILITY_THREAT_THRESHOLD = 25;

/**
 * Divisor for the distance component of corruption.
 * corruption = (distance / CORRUPTION_DISTANCE_DIVISOR) × EVIL_WEIGHT[biome]
 *
 * At d=50 plain biome: 50/50 × 0.1 = 0.1 (near-origin tame, very low)
 * At d=200 mountain:   200/50 × 0.5 = 2.0 (deep frontier, significant)
 * At d=300 mountain:   300/50 × 0.5 = 3.0 (always decaying)
 */
export const CORRUPTION_DISTANCE_DIVISOR = 50;

/**
 * Scales `ticksSinceFall × localPressure` into a continuous accumulated-evil
 * value. One ACCUMULATED_EVIL_DIVISOR ticks of unit-pressure evil = +1 raw
 * accumulated evil unit, which maps to roughly one extra boss-tier step.
 */
export const ACCUMULATED_EVIL_DIVISOR = 200;

// ─── world-state interface ───────────────────────────────────────────────────

/**
 * Read-only slice of world knowledge needed for pressure calculation.
 * Passed in by the caller (App.ts) so evilPressure stays dependency-free.
 */
export interface EvilPressureWorldState {
  /** World seed — needed by neighboursWithin50 for deterministic neighbour lookup. */
  readonly worldSeed: number;
  /** Current world-clock tick (from WorldClock.tick). */
  readonly worldTick: number;
  /**
   * Look up a town's liberation state by coords. Returns null if the coords
   * don't point at a known town tile (unloaded chunk, non-town tile, etc.).
   */
  getTownState(q: number, r: number): TownState | null;
}

// ─── pure formulas ───────────────────────────────────────────────────────────

/**
 * Intrinsic corruption for a town — the floor of Evil Pressure that prevents
 * the frontier from ever being permanently safe.
 *
 * Uses EVIL_WEIGHT (fractional, 0.1–0.5) from biome-transition.ts — the
 * canonical source for a continuous scalar. See module header for why this is
 * distinct from BIOME_EVIL_WEIGHT in bossEncounter.ts.
 */
export function corruption(town: Tile): number {
  const d = originDistance(town.q, town.r);
  return (d / CORRUPTION_DISTANCE_DIVISOR) * EVIL_WEIGHT[town.biome];
}

/**
 * Evil Pressure on `town` at the moment of evaluation.
 *
 * pressure = evilNeighbours − liberatedNeighbours + corruption(town)
 *
 * 'under-threat' towns count as liberated for the neighbour tally (they still
 * push back against pressure). A negative pressure means the cluster is
 * self-sustaining and Stability regenerates (capped at STABILITY_MAX).
 */
export function computePressure(town: Tile, worldState: EvilPressureWorldState): number {
  const neighbours = neighboursWithin50(town.q, town.r, worldState.worldSeed);
  let evil = 0;
  let liberated = 0;
  for (const n of neighbours) {
    if (n.q === town.q && n.r === town.r) continue; // exclude self
    const state = worldState.getTownState(n.q, n.r);
    if (state === 'evil') evil++;
    else if (state === 'liberated' || state === 'under-threat') liberated++;
    // null = unknown neighbour (unloaded chunk) → ignored
  }
  return evil - liberated + corruption(town);
}

/**
 * Stability drains by `pressure × ticksElapsed` since the last cached
 * evaluation. Returns the raw new stability — may exceed STABILITY_MAX (when
 * pressure is negative and stability regenerates) or go below 0 (fallen).
 * Callers must clamp and derive state via `townStateFromStability`.
 */
export function computeCurrentStability(town: Tile, currentTick: number, pressure: number): number {
  const state = town.lifecycleState as TownLifecycleState | null;
  const cached = state?.stability ?? STABILITY_MAX;
  const lastAt = state?.stabilityLastComputedAt ?? currentTick;
  const ticksElapsed = Math.max(0, currentTick - lastAt);
  return cached - pressure * ticksElapsed;
}

/**
 * Derive the appropriate TownState from a stability value.
 * Values above S_MAX are clamped to 'liberated' (regeneration case).
 */
export function townStateFromStability(stability: number): TownState {
  if (stability <= 0) return 'evil';
  if (stability < STABILITY_THREAT_THRESHOLD) return 'under-threat';
  return 'liberated';
}

/**
 * Continuous accumulated-evil score: grows the longer a fallen town sits
 * uncontested. Returned in "tier units" — divide by ACCUMULATED_EVIL_DIVISOR
 * before adding to tier index so it scales proportionally.
 *
 * Returns 0 for a town that has never fallen (no tickFallen persisted).
 */
export function accumulatedEvil(town: Tile, currentTick: number, localPressure: number): number {
  const state = town.lifecycleState as TownLifecycleState | null;
  const tickFallen = state?.tickFallen;
  if (tickFallen === undefined) return 0;
  const ticksSinceFall = Math.max(0, currentTick - tickFallen);
  return (ticksSinceFall * Math.max(0, localPressure)) / ACCUMULATED_EVIL_DIVISOR;
}

// ─── lazy evaluation + mutation ───────────────────────────────────────────────

/**
 * Result of a lazy pressure evaluation.
 * Callers use this to mutate the town tile's lifecycle blob and persist.
 */
export interface StabilityUpdate {
  /** New (clamped) stability to store. */
  stability: number;
  /** Derived town state based on the new stability. */
  townState: TownState;
  /** Tick at which tickFallen should be recorded (only when falling to evil). */
  tickFallen?: number;
}

/**
 * Evaluate the lazy stability for a liberated (or under-threat) town and
 * return the updated values to persist. Returns null for:
 *   - the spawn town (hard-immune)
 *   - towns already in 'evil' state (no drain needed)
 *   - towns with no lifecycle state
 *
 * Does NOT mutate the tile or persist — the caller handles that so this
 * function stays pure and testable.
 */
export function evaluateTownStability(
  town: Tile,
  worldState: EvilPressureWorldState,
): StabilityUpdate | null {
  if (town.type !== 'town') return null;
  if (isSpawnTown(town.q, town.r)) return null; // hard-immune

  const state = town.lifecycleState as TownLifecycleState | null;
  if (state === null) return null;

  const currentTownState = getTownState(town);
  // Only drain liberated and under-threat towns; evil towns skip (already fallen).
  if (currentTownState === 'evil') return null;

  const pressure = computePressure(town, worldState);
  const rawStability = computeCurrentStability(town, worldState.worldTick, pressure);
  const clamped = Math.min(STABILITY_MAX, rawStability);
  const newState = townStateFromStability(clamped);

  const update: StabilityUpdate = {
    stability: Math.max(0, clamped),
    townState: newState,
  };

  // Record the fall tick so accumulatedEvil can scale boss difficulty later.
  if (newState === 'evil') {
    update.tickFallen = worldState.worldTick;
  }

  return update;
}
